import React, { FormEvent, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-query';
import { InviteCARequest } from 'api/companyOnboarding';
import api from 'api';
import { fetchLoggedInUser } from 'reducers/loggedInUser';
import { AppError } from 'utils/AppError';
import Errors from 'components/ui/Form/Errors';
import { EMAIL_REGEX } from 'utils/regexes';
import { trackSegment } from 'utils/segment';
import successBg from 'assets/image--success-tick-with-bg.png';
import { getStaticMediaUrl } from '../../../../../utils/Urls';

type InviteCAModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
};

const InviteCAModal = ({ isOpen, onDismiss }: InviteCAModalProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isInviteSuccess, setIsInviteSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useDispatch();

  const inviteCAMutation = useMutation(
    ({ name, email, phone }: InviteCARequest) =>
      api.companyOnboarding.inviteCA({ name, email, phone }),
    {
      onSuccess: () => {
        dispatch(fetchLoggedInUser());
        setIsInviteSuccess(true);
      },
      onError: (_error: typeof AppError) => {},
    },
  );

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    trackSegment('onboarding.setup_compliance.tds.invite_your_ca_click');
    inviteCAMutation.mutate({ name, email, phone });
  };

  const handleDismiss = () => {
    onDismiss();
    setName('');
    setEmail('');
    setPhone('');
    setIsInviteSuccess(false);
    inviteCAMutation.reset();
  };

  const isFormBeingSubmitted = inviteCAMutation.status === 'loading';
  const isEmailInvalid = email.length > 0 && !new RegExp(EMAIL_REGEX).test(email);

  if (isInviteSuccess) {
    return (
      <Modal
        isOpen={isOpen}
        onDismiss={handleDismiss}
        accessibilityLabel="Invite CA success"
      >
        <ModalBody>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.5"
            padding="spacing.5"
          >
            <img
              src={getStaticMediaUrl(successBg)}
              alt="invite-success"
              width={120}
            />
            <Heading size="medium" textAlign="center">
              Invitation email is sent successfully to your Chartered Accountant
            </Heading>
            <Text size="large" color="surface.text.gray.muted" textAlign="center">
              Your Chartered Accountant can login to XPayroll via the invitation email and help
              in account setup
            </Text>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <Button variant="primary" onClick={handleDismiss}>
              Okay
            </Button>
          </Box>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size="small"
      accessibilityLabel="Invite Chartered Accountant"
    >
      <ModalHeader title="Invite your Chartered Accountant" />
      <ModalBody>
        <form ref={formRef} onSubmit={handleSubmit} style={{ width: '100%' }}>
          <Box display="flex" flexDirection="column" gap="spacing.5" width="100%">
            {inviteCAMutation.status === 'error' && (
              <Alert
                color="negative"
                description={<Errors errorResponse={inviteCAMutation.error} />}
                isDismissible={false}
                isFullWidth
              />
            )}
            <TextInput
              label="Name"
              name="ca-name"
              placeholder="John Doe"
              isRequired
              necessityIndicator="required"
              isDisabled={isFormBeingSubmitted}
              value={name}
              onChange={({ value }) => setName(value ?? '')}
            />
            <TextInput
              label="Email address"
              name="ca-email"
              type="email"
              placeholder="john.doe@example.com"
              isRequired
              necessityIndicator="required"
              helpText="Invitee will have admin access. You can always change the user role later!"
              validationState={isEmailInvalid ? 'error' : 'none'}
              errorText="Please enter a valid email address"
              isDisabled={isFormBeingSubmitted}
              value={email}
              onChange={({ value }) => setEmail(value ?? '')}
            />
            <TextInput
              label="Phone number"
              name="ca-phone"
              type="telephone"
              placeholder="eg. 9810012345"
              isDisabled={isFormBeingSubmitted}
              value={phone}
              onChange={({ value }) => setPhone(value ?? '')}
            />
          </Box>
        </form>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" width="100%">
          <Button
            variant="primary"
            isLoading={isFormBeingSubmitted}
            onClick={() => formRef.current?.requestSubmit()}
          >
            Send invite
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};

const InviteCA = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Card elevation="none" borderRadius="large" backgroundColor="surface.background.gray.intense">
        <CardBody>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap="spacing.5"
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
              gap="spacing.1"
              flex={1}
            >
              <Text size="large" weight="semibold" textAlign="left">
                Don't have compliance details?
              </Text>
              <Text size="medium" color="surface.text.gray.muted" textAlign="left">
                Invite your CA to setup your tax and TRACES
              </Text>
            </Box>
            <Button variant="primary" onClick={() => setIsModalOpen(true)}>
              Invite Now
            </Button>
          </Box>
        </CardBody>
      </Card>
      <InviteCAModal isOpen={isModalOpen} onDismiss={() => setIsModalOpen(false)} />
    </>
  );
};

export default InviteCA;
